import { useLocalStorage } from '@uidotdev/usehooks';
import { createContext, useContext } from 'react';
import { CompletionType } from '~/types';

export const SPARK_SORT_TYPES = {
  favorite: 'favorite',
  frequent: 'frequent',
  alphabetically: 'alphabetically',
  new_to_old: 'new_to_old',
  default: 'default',
} as const;

export type SortType = (typeof SPARK_SORT_TYPES)[keyof typeof SPARK_SORT_TYPES];

const sortCompletionTypesAlphabetically = (completionTypes: CompletionType[]): CompletionType[] =>
  completionTypes.sort((a, b) => {
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();

    if (nameA < nameB) {
      return -1;
    } else if (nameA > nameB) {
      return 1;
    } else {
      return 0;
    }
  });

const sortCompletionTypesByCreationDate = (completionTypes: CompletionType[]): CompletionType[] =>
  completionTypes.sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);

    return dateB.getTime() - dateA.getTime();
  });

const sortCompletionTypesByFavorite = (completionTypes: CompletionType[]): CompletionType[] =>
  completionTypes.sort((a, b) => b.favoriteCompletionTypes.length - a.favoriteCompletionTypes.length);

const sortCompletionTypesDefault = (completionTypes: CompletionType[]): CompletionType[] =>
  completionTypes.sort((a, b) => {
    // If both have expiration dates or both don't, maintain original order
    if (!!a.expiresAt === !!b.expiresAt) {
      return a.order - b.order;
    }
    // If only one has expiration date, it should come first
    return a.expiresAt ? -1 : 1;
  });

const sortCompletionTypesByUsage = (completionTypes: CompletionType[]): CompletionType[] =>
  completionTypes.sort((a, b) => {
    const usageCountA = a.usages[0]?.usageCount ?? 0;
    const usageCountB = b.usages[0]?.usageCount ?? 0;

    return usageCountB - usageCountA;
  });

const getCompletionsBySortType = (sortType: SortType, completionTypes: CompletionType[]): CompletionType[] => {
  switch (sortType) {
    case SPARK_SORT_TYPES.favorite:
      return sortCompletionTypesByFavorite(completionTypes);
    case SPARK_SORT_TYPES.frequent:
      return sortCompletionTypesByUsage(completionTypes);
    case SPARK_SORT_TYPES.alphabetically:
      return sortCompletionTypesAlphabetically(completionTypes);
    case SPARK_SORT_TYPES.new_to_old:
      return sortCompletionTypesByCreationDate(completionTypes);
    case SPARK_SORT_TYPES.default:
      return sortCompletionTypesDefault(completionTypes);
  }
};

const SortedCompletionTypesContext = createContext<{
  sortedCompletionTypes: CompletionType[];
  sortCompletionTypesBy: (sortType: SortType) => void;
  defaultSortType: SortType;
} | null>(null);

export const SortedCompletionTypesProvider = ({
  children,
  completionTypes,
}: {
  children: React.ReactNode;
  completionTypes: CompletionType[];
}) => {
  const [sortType, setSortType] = useLocalStorage<SortType>('spark_sort', SPARK_SORT_TYPES.default);

  const sortCompletionTypesBy = (sortType: SortType) => {
    setSortType(sortType);
  };

  const sortedCompletionTypes = getCompletionsBySortType(sortType, completionTypes);

  return (
    <SortedCompletionTypesContext.Provider
      value={{ sortedCompletionTypes, sortCompletionTypesBy, defaultSortType: sortType }}
    >
      {children}
    </SortedCompletionTypesContext.Provider>
  );
};

export const useSortedCompletionTypes = () => {
  const context = useContext(SortedCompletionTypesContext);

  if (!context) throw new Error('useSortCompletionTypes must be used within an SortedCompletionTypesProvider');

  return context;
};
